import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Clock, Coffee, MapPin, Monitor, Users } from "lucide-react";
import billingService from "../../services/billingService";
import logger from "../../utils/logger";
import SEO from "../../components/SEO/SEO";
import LandingHero from "../../components/landing/LandingHero";
import BenefitGrid from "../../components/landing/BenefitGrid";
import BookingSteps from "../../components/landing/BookingSteps";
import TeamsSection from "../../components/landing/TeamsSection";
import { FinalCtaCard, PartnerSection } from "../../components/landing/PartnerAndCta";
import WorkSearchBar from "./components/WorkSearchBar";
import WorkspaceTypes from "./components/WorkspaceTypes";
import type { ListingSubcategory } from "../../components/landing/ListingGrid";
import "./index.css";

const BENEFITS = [
  { icon: Clock, title: "Flexibility", description: "Access workspace without committing to a permanent office." },
  {
    icon: Monitor,
    title: "Productivity",
    description: "Business-ready environments designed for focus and collaboration.",
  },
  {
    icon: MapPin,
    title: "Convenience",
    description: "Real-time availability and convenient locations across the city.",
  },
  {
    icon: Users,
    title: "Community",
    description: "Connect with a wider professional community of like-minded people.",
  },
  {
    icon: Coffee,
    title: "Choice",
    description: "Coworking, desks, meeting rooms, conference rooms and private offices.",
  },
];

const STEPS = [
  { title: "Discover", description: "Find a workspace that fits your needs, location and schedule." },
  { title: "Check Availability", description: "See real-time availability before you commit to anything." },
  { title: "Book", description: "Confirm your booking and pay securely online in under two minutes." },
  { title: "Get to Work", description: "Arrive at your workspace and get straight to it." },
];

export default function WorkLandingScreen() {
  const history = useHistory();
  const [subcategories, setSubcategories] = useState<ListingSubcategory[]>([]);

  // Work category is ID 1 in the public registry
  useEffect(() => {
    billingService
      .getPublicServiceSubcategories(1)
      .then((res: any) => {
        const subs: any[] = Array.isArray(res.data)
          ? res.data
          : res.data?.data || res.data?.content || res.data?.items || [];
        if (subs.length > 0) setSubcategories(subs);
      })
      .catch((err: any) => logger.warn("WorkLanding: failed loading subcategories for category 1:", err));
  }, []);

  return (
    <div className="work-landing-container min-h-screen flex flex-col">
      <SEO
        title="Work Wherever You Need To - SPACIA WORK"
        description="Give yourself, or your team, the flexibility to work from anywhere without sacrificing the tools and environment you need to be productive."
      />

      <LandingHero
        image="/figma-assets/work/hero.jpg"
        imageAlt="Contemporary workspace"
        badge="SPACIA WORK"
        titleLines={["Work Wherever", "You Need To"]}
        lead="Give yourself, or your team, the flexibility to work from anywhere without sacrificing the tools and environment you need to be productive."
        primaryCta={{ label: "Explore Workspaces", onClick: () => history.push("/workspaces-search?type=work") }}
        secondaryCta={{ label: "List Your Space", onClick: () => history.push("/partner-signup") }}
        desktopHeight={872}
        search={<WorkSearchBar subcategories={subcategories} />}
      />
      <WorkspaceTypes subcategories={subcategories} />
      <BenefitGrid eyebrow="Why SPACIA WORK" title="Work Without the Commitment" items={BENEFITS} />
      <BookingSteps eyebrow="Simple Process" title="How Work Booking Works" steps={STEPS} />
      <TeamsSection
        title="Flexible Workspace for Your Team"
        description="Give your people flexible access to workspaces across every location you operate in."
        uses={[
          "Team meetings",
          "Offsites",
          "Client meetings",
          "Flexible workspace",
          "Private offices",
          "Conference rooms",
        ]}
        image="/figma-assets/work/teams.jpg"
        imageAlt="Team working together"
      />
      <PartnerSection
        eyebrow="For Space Partners"
        title="Have a Space to Share?"
        description="SPACIA allows workspace operators to list their spaces and reach professionals, businesses and enterprise clients looking for flexible work environments."
        tags={["Coworking spaces", "Meeting facilities", "Conference venues", "Private offices"]}
        image="/figma-assets/work/space-partners.jpg"
        imageAlt="Workspace ready to list"
      />
      <FinalCtaCard
        eyebrow="Get Started"
        title="Find a Space That Works for You"
        description="Whether you need a desk for the day, a room for a meeting or a private office for your team, SPACIA helps you find the right space without the long-term commitment."
        primaryCta={{ label: "Explore Workspaces", onClick: () => history.push("/workspaces-search?type=work") }}
        secondaryCta={{ label: "List Your Space", onClick: () => history.push("/partner-signup") }}
      />
    </div>
  );
}
